import { Suspense, lazy, type ReactNode } from 'react';
import { useFlowStore } from '@/features/configurator/flow.store';
import type { FlowStep } from '@/features/configurator/flow-plan';
import { COPY } from '@/shared/content/copy';
import { garmentOf } from '@/shared/content/garments';
import { shopPrintFor, type Brand } from '@/shared/content/brand';
import type { Print, Product } from '@/shared/api/schemas';
import styles from './screen-router.module.scss';

// Экраны конструктора грузятся отдельно: покупатель готового мерча
// никогда их не откроет и не должен за них платить трафиком.
// Обложка грузится сразу вместе с первым кадром: она и есть первый кадр,
// и откладывать её значит показать пустой модуль на долю секунды.
const CoverScreen = lazy(() =>
  import('@/screens/cover/CoverScreen').then((m) => ({ default: m.CoverScreen })),
);
const BaseScreen = lazy(() =>
  import('@/screens/base/BaseScreen').then((m) => ({ default: m.BaseScreen })),
);
const DoneScreen = lazy(() =>
  import('@/screens/done/DoneScreen').then((m) => ({ default: m.DoneScreen })),
);
const PetScreen = lazy(() =>
  import('@/screens/pet/PetScreen').then((m) => ({ default: m.PetScreen })),
);
const ShopScreen = lazy(() =>
  import('@/screens/shop/ShopScreen').then((m) => ({ default: m.ShopScreen })),
);
const StoreScreen = lazy(() =>
  import('@/screens/store/StoreScreen').then((m) => ({ default: m.StoreScreen })),
);
const ItemScreen = lazy(() =>
  import('@/screens/item/ItemScreen').then((m) => ({ default: m.ItemScreen })),
);

const StudioScreen = lazy(() =>
  import('@/screens/studio/StudioScreen').then((m) => ({ default: m.StudioScreen })),
);
const ArtistsScreen = lazy(() =>
  import('@/screens/artists/ArtistsScreen').then((m) => ({ default: m.ArtistsScreen })),
);
const CatalogScreen = lazy(() =>
  import('@/screens/catalog/CatalogScreen').then((m) => ({ default: m.CatalogScreen })),
);
const PrintsScreen = lazy(() =>
  import('@/screens/prints/PrintsScreen').then((m) => ({ default: m.PrintsScreen })),
);
const ProductScreen = lazy(() =>
  import('@/screens/product/ProductScreen').then((m) => ({ default: m.ProductScreen })),
);
const BuilderScreen = lazy(() =>
  import('@/screens/builder/BuilderScreen').then((m) => ({ default: m.BuilderScreen })),
);
const LibraryScreen = lazy(() =>
  import('@/screens/library/LibraryScreen').then((m) => ({ default: m.LibraryScreen })),
);
const PlacementScreen = lazy(() =>
  import('@/screens/placement/PlacementScreen').then((m) => ({ default: m.PlacementScreen })),
);
const ResultScreen = lazy(() =>
  import('@/screens/result/ResultScreen').then((m) => ({ default: m.ResultScreen })),
);
const SitePayScreen = lazy(() =>
  import('@/screens/pay/SitePayScreen').then((m) => ({ default: m.SitePayScreen })),
);
const OrderScreen = lazy(() =>
  import('@/screens/order/OrderScreen').then((m) => ({ default: m.OrderScreen })),
);

interface Props {
  partnerSlug: string;
  products: Product[];
  /** Принты выбранного артиста — для витрины. */
  prints: Print[];
  /** Вся библиотека партнёра — для добавления второго принта. */
  allPrints: Print[];
  product: Product;
  artists: string[];
  color: string;
  quantities: Record<string, number>;
  /** Оформление и тексты партнёра: логотип, заголовки обложки. */
  brand: Brand;
  onPickArtist: (artist: string) => void;
  onBuild: () => void;
  onPickProduct: (product: Product) => void;
  onColorChange: (color: string) => void;
  onQuantityChange: (size: string, qty: number) => void;
  /** Вернуться к витрине из состава заказа. */
  onBackToCatalog: () => void;
  /**
   * Свои экраны вместо общих — по одному шагу.
   *
   * Нужно там, где шаг называется так же, а делает другое: заказ на сайте
   * заканчивается отправкой, а в мини-приложении — оплатой. Разводить это
   * условием «если телеграм» внутри экрана нельзя: экран не должен знать,
   * где он открыт. Разводить копией маршрутизатора — тем более.
   */
  screens?: Partial<Record<FlowStep, ReactNode>>;
}

export function ScreenRouter({
  partnerSlug,
  products,
  prints,
  allPrints,
  product,
  artists,
  color,
  quantities,
  brand,
  onPickArtist,
  onBuild,
  onPickProduct,
  onColorChange,
  onQuantityChange,
  onBackToCatalog,
  screens,
}: Props) {
  const step = useFlowStore((s) => s.step);
  const direction = useFlowStore((s) => s.direction);
  const next = useFlowStore((s) => s.next);

  return (
    // Ключ по шагу заставляет обёртку пересоздаться на каждом переходе —
    // именно это запускает появление заново. Без него React переиспользует
    // узел, и новый экран возникает мгновенно, будто щёлкнули выключателем.
    <Suspense fallback={<p className={styles['ls-router__state']}>{COPY.common.loading}</p>}>
      <div
        className={[styles['ls-router__screen'], styles[`ls-router__screen--is-${direction}`]].join(
          ' ',
        )}
        key={step}
      >
        {step === 'cover' && (
          <CoverScreen
            imageUrl={brand.coverImageUrl}
            logoUrl={brand.logoUrl}
            overline={brand.overline}
            title={brand.coverTitle}
            lead={brand.coverLead}
          />
        )}
        {step === 'shop' && (
          <ShopScreen
            products={products}
            logoUrl={brand.logoMiniUrl}
            title={brand.shopTitle}
            lead={brand.shopLead}
            prints={brand.shopPrints}
            isPrintLight={brand.isShopPrintLight}
            onPick={onPickProduct}
            onBuild={onBuild}
          />
        )}
        {step === 'store' && (
          <StoreScreen
            products={products}
            title={brand.shopTitle}
            customGarments={brand.customGarments}
            onPick={onPickProduct}
            onBuild={onPickProduct}
          />
        )}
        {step === 'item' && (
          <ItemScreen product={product} color={color} onColorChange={onColorChange} />
        )}

        {step === 'studio' && (
          <StudioScreen
            product={product}
            prints={allPrints}
            color={color}
            priceRub={brand.printPriceRub ?? product.price_rub}
            onColorChange={onColorChange}
          />
        )}
        {step === 'pet' && (
          <PetScreen
            product={product}
            color={color}
            quantities={quantities}
            logoUrl={brand.logoMiniUrl}
            priceRub={brand.printPriceRub ?? product.price_rub}
            onColorChange={onColorChange}
            onQuantityChange={onQuantityChange}
          />
        )}
        {step === 'base' && (
          <BaseScreen
            product={product}
            color={color}
            quantities={quantities}
            logoUrl={brand.logoMiniUrl}
            // На шаге основы цвет уже выбран человеком, и печатный файл
            // определяется им же: на розовой ткани сосиски белые.
            printUrl={shopPrintFor(brand, color)}
            priceRub={product.price_rub}
            onColorChange={onColorChange}
            onQuantityChange={onQuantityChange}
          />
        )}
        {step === 'done' &&
          (screens?.done ?? (
            <DoneScreen
              logoUrl={brand.logoMiniUrl}
              miniappUrl={brand.miniappUrl}
              onBackToCatalog={onBackToCatalog}
              // Оплата на месте: «Оплатить» ведёт к форме и кассе в виджете,
              // а не в мини-приложение.
              onAction={brand.sitePayment ? next : undefined}
              lead={brand.sitePayment ? 'Укажи, куда доставить, и оплати картой' : undefined}
            />
          ))}
        {step === 'artists' && <ArtistsScreen artists={artists} onPick={onPickArtist} />}
        {step === 'catalog' && <CatalogScreen products={products} onPick={onPickProduct} />}
        {step === 'prints' && (
          <PrintsScreen
            prints={prints}
            garmentType={garmentOf(product.product_type).type}
            color={color}
          />
        )}
        {step === 'product' && <ProductScreen product={product} previewUrls={[]} />}
        {step === 'builder' && (
          <BuilderScreen
            product={product}
            color={color}
            quantities={quantities}
            onColorChange={onColorChange}
            onQuantityChange={onQuantityChange}
          />
        )}
        {step === 'library' && <LibraryScreen prints={allPrints} />}
        {step === 'placement' && (
          <PlacementScreen
            product={product}
            color={color}
            quantities={quantities}
            onColorChange={onColorChange}
            onQuantityChange={onQuantityChange}
          />
        )}
        {step === 'result' && (
          <ResultScreen
            product={product}
            color={color}
            quantities={quantities}
            onColorChange={onColorChange}
            onQuantityChange={onQuantityChange}
          />
        )}
        {step === 'order' &&
          (screens?.order ??
            (brand.sitePayment ? (
              <SitePayScreen
                partnerSlug={partnerSlug}
                logoUrl={brand.logoMiniUrl}
                onBackToCatalog={onBackToCatalog}
              />
            ) : (
              <OrderScreen partnerSlug={partnerSlug} />
            )))}
      </div>
    </Suspense>
  );
}
